import { useCallback, useContext, useEffect } from 'react';
import { useScreenSize } from '@banhala/ui-kit';
import { ScrollContext } from '../context/ScrollContext';
import { type View } from 'react-native';
import {
  measure,
  useAnimatedRef,
  useFrameCallback,
  useSharedValue,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { useIsForegroundNow } from 'shared/src/hooks';
import {
  retainedState,
  useRetainedState,
} from 'shared/src/hooks/retainedState';

const THROTTLE_MS = 50;
const MIN_VIEW_TIME_MS = 500;
// 두 체크 사이 pageY 이동이 이보다 크면 "레이아웃 이동 중"으로 보고 dwell 리셋(px).
// (예: 상단 WebView 가 200→1556 으로 커지며 아래 섹션 pageY 가 흔들리는 구간)
const POSITION_STABLE_EPS = 4;

// ── 지연 mount 게이트(useMountWhenNearViewport) 전용 튜닝 ──────────────────────
// mount 는 "화면에 닿기 전에" 미리 시켜야 blank/프레임드랍이 없다. DISPLAY 추적보다 공격적으로
// 앞당긴다: (1) 더 자주 체크 (2) 짧은 dwell (3) 큰 runway. mount 확정 후엔 콜백을 끄므로 상시 부하 X.
const MOUNT_CHECK_THROTTLE_MS = 16; // ~매 프레임 근접 체크 (기존 50ms → 감지 지연 축소)
const MOUNT_NEAR_DWELL_MS = 16; // near 확인 시간(≈1틱). 스크롤 중엔 moving 리셋이 없어 사실상 즉시 mount
const DEFAULT_SCREENS_AHEAD = 2.5; // 뷰포트 ±2.5화면 runway (기존 1.5 → 훨씬 앞당겨 mount)

// 이번 visit 에 노출된 섹션 key 집합 (remount 가로질러 DISPLAY 재발화 방지).
const sectionVisibleState = retainedState<Set<string>>(() => new Set());

// 이번 visit 에 mount 확정된 섹션 key 집합. mount 여부를 로컬 useState 로 두면 InactiveScreenGate
// 가 null 을 렌더한 뒤 remount 될 때 false 로 초기화되어, 이미 mount 됐던 섹션이 다시 unmount
// 됐다가 재감지되는 문제가 있어 visit 단위로 유지한다. (RetainedStateProvider 는 gate 바깥)
const sectionMountedState = retainedState<Set<string>>(() => new Set());

export const useIsSectionVisible = (key: string) => {
  const [visible] = useRetainedState(sectionVisibleState);
  return visible.has(key);
};

export const useMarkSectionVisible = () => {
  const [, setVisible] = useRetainedState(sectionVisibleState);
  return useCallback(
    (key: string) => {
      // 동일 참조 반환 시 useSyncExternalStore 가 리렌더 생략. 변경 시에만 새 Set.
      setVisible(prev => (prev.has(key) ? prev : new Set(prev).add(key)));
    },
    [setVisible],
  );
};

/**
 * 컴포넌트가 뷰포트에 진입했는지 감지하는 훅.
 * 중첩된 서브 섹션 컴포넌트에서 사용.
 *
 * 측정·dwell 판정을 전부 UI 스레드에서 수행한다(useFrameCallback + reanimated measure).
 * 연속 MIN_VIEW_TIME_MS 동안 가시 상태였으면 mark — "잠깐 스쳐 지나간" 케이스에서
 * 가짜 DISPLAY가 발화되는 것을 방지. 중간에 viewport를 벗어나면 visibleStart 리셋.
 * 100ms throttle, mark 이후 SharedValue 게이트로 즉시 early return.
 *
 * ⚠️ 메모리: viewRef 는 useAnimatedRef(=tag 기반 shareable)라 worklet 이 캡처해도
 * 컴포넌트(fiber)를 retain 하지 않는다. JS 로 넘기는 건 markVisible(setVisible)뿐이라
 * `__remoteFunctionRegistry` 가 트리를 붙들지 않는다.
 * (이전 구현은 `scheduleOnRN(checkVisibility)` 가 viewRef→View→fiber 를 캡처해
 *  PDP 트리 전체가 누수됐음 — 섹션 수만큼 ×N)
 */
export const useElementViewportTracking = (
  visibilityKey: string,
  offset = 0,
) => {
  const { height: screenHeight } = useScreenSize();
  const viewRef = useAnimatedRef<View>();
  const markVisible = useMarkSectionVisible();
  const isMarkedSV = useSharedValue(false);
  const visibleStartSV = useSharedValue(0);
  const lastCheckSV = useSharedValue(0);

  const frameCallback = useFrameCallback(frameInfo => {
    if (isMarkedSV.value) {
      return;
    }
    const now = frameInfo.timestamp;
    if (now - lastCheckSV.value < THROTTLE_MS) {
      return;
    }
    lastCheckSV.value = now;
    // 뷰가 아직 레이아웃 안 된 프레임엔 native measure 가 throw 할 수 있어 가드.
    // (worklet 에선 animatedRef.current 가 신뢰 불가 → try/catch 로 방어)
    let measured: ReturnType<typeof measure> = null;
    try {
      measured = measure(viewRef);
    } catch (_error) {
      return;
    }
    if (measured === null) {
      return;
    }
    const isInViewport =
      measured.height > 0 &&
      measured.pageY + measured.height > 0 &&
      measured.pageY < screenHeight - offset;

    if (!isInViewport) {
      visibleStartSV.value = 0;
      return;
    }
    if (visibleStartSV.value === 0) {
      visibleStartSV.value = now;
    } else if (now - visibleStartSV.value >= MIN_VIEW_TIME_MS) {
      isMarkedSV.value = true;
      scheduleOnRN(markVisible, visibilityKey);
    }
  });

  // 화면이 가려지면(다른 화면 push / 백그라운드) frame callback 을 멈춘다.
  // 가려진 동안 measure()가 not-rendered 뷰를 계속 측정하면 reanimated 가 undefined
  // LayoutMetrics 경고를 쏟다가 끝내 libworklets 네이티브 assert(getObject)로 크래시한다.
  // (JS try/catch 는 네이티브 abort 를 못 막는다.) 노출 판정은 화면이 보일 때만 의미 있으므로
  // 가려진 동안 비활성화해도 무방하고, CPU(매 프레임 measure)도 아낀다.
  const isForeground = useIsForegroundNow();
  useEffect(() => {
    frameCallback.setActive(isForeground);
  }, [frameCallback, isForeground]);

  return viewRef;
};

/**
 * 뷰포트에 "곧 진입"하면 mount 시키기 위한 훅 (지연 mount 게이트).
 *
 * `screensAhead`(기본 2.5화면) runway 를 미리 포함해 **화면에 닿기 한참 전에 mount**한다
 * (스크롤 도달 전 mount → blank/프레임 드랍 방지). `MOUNT_CHECK_THROTTLE_MS`(≈매 프레임)로
 * 빠르게 감지하고, `MOUNT_NEAR_DWELL_MS`(≈1틱) 확인 후 mount 한다.
 * 위치안정성(pageY 이동) 게이트는 **스크롤 중이 아닐 때만** 적용해(초기/비동기 레이아웃, 상단
 * WebView 성장 등의 첫 프레임 오판만 거른다), **스크롤 중엔 리셋 없이 runway 진입 즉시 mount**된다.
 * 한 번 mount 되면 유지(persist)하고 frame callback 을 중단해 CPU 를 아낀다(재mount/thrash 방지).
 *
 * 반환한 ref 는 자리표시(placeholder) 요소에 달아 위치를 측정한다(높이 0 이어도 pageY 로 판정).
 * leak 안전성은 useElementViewportTracking 과 동일(scheduleOnRN 은 setState 만 캡처).
 */
export const useMountWhenNearViewport = (
  mountKey: string,
  screensAhead = DEFAULT_SCREENS_AHEAD,
): readonly [ReturnType<typeof useAnimatedRef<View>>, boolean] => {
  const { height: screenHeight } = useScreenSize();
  // 스크롤 중 pageY 이동은 "레이아웃 리플로우"가 아니라 정상 스크롤이라 dwell 을 리셋하면
  // 안 된다(리셋하면 스크롤이 멈춰야 mount → 늦음). isScrolling 으로 둘을 구분한다
  // (scrollY/isScrolling 모두 SharedValue → worklet 에서 읽음).
  const { isScrolling } = useContext(ScrollContext);
  const viewRef = useAnimatedRef<View>();
  // mount 확정 여부를 visit 단위 retained 상태로 유지(InactiveScreenGate null→remount 후에도 보존).
  const [mountedKeys, setMountedKeys] = useRetainedState(sectionMountedState);
  const mounted = mountedKeys.has(mountKey);
  const markMounted = useCallback(() => {
    setMountedKeys(prev =>
      prev.has(mountKey) ? prev : new Set(prev).add(mountKey),
    );
  }, [setMountedKeys, mountKey]);
  const doneSV = useSharedValue(false);
  const lastCheckSV = useSharedValue(0);
  const nearStartSV = useSharedValue(0);
  const lastYSV = useSharedValue(Number.NaN);

  const frameCallback = useFrameCallback(frameInfo => {
    if (doneSV.value) {
      return;
    }
    const now = frameInfo.timestamp;
    if (now - lastCheckSV.value < MOUNT_CHECK_THROTTLE_MS) {
      return;
    }
    lastCheckSV.value = now;
    let measured: ReturnType<typeof measure> = null;
    try {
      measured = measure(viewRef);
    } catch (_error) {
      return;
    }
    if (measured === null) {
      return;
    }
    const runway = screenHeight * screensAhead;
    // 뷰포트 ± runway 안에 걸치면 "곧 진입"으로 본다 (placeholder 높이 0 도 pageY 로 판정).
    const near =
      measured.pageY + measured.height > -runway &&
      measured.pageY < screenHeight + runway;
    // "스크롤 중이 아닌데" pageY 가 크게 움직였으면(상단 WebView 성장 등) 레이아웃 리플로우 →
    // 오판 방지로 dwell 리셋. 스크롤 중 이동은 정상이므로 리셋하지 않아 runway 진입 즉시 mount 된다.
    const moving =
      !isScrolling?.value &&
      !Number.isNaN(lastYSV.value) &&
      Math.abs(measured.pageY - lastYSV.value) > POSITION_STABLE_EPS;
    lastYSV.value = measured.pageY;

    if (!near || moving) {
      nearStartSV.value = 0; // 근접 아님 / 이동 중 → dwell 리셋
      return;
    }
    if (nearStartSV.value === 0) {
      nearStartSV.value = now;
    } else if (now - nearStartSV.value >= MOUNT_NEAR_DWELL_MS) {
      doneSV.value = true;
      scheduleOnRN(markMounted);
    }
  });

  const isForeground = useIsForegroundNow();
  useEffect(() => {
    // mount 확정 후엔 콜백 중단(persist + CPU 절약). 가려진 동안도 중단.
    frameCallback.setActive(isForeground && !mounted);
  }, [frameCallback, isForeground, mounted]);

  return [viewRef, mounted] as const;
};
